import { Params } from '@angular/router';
import {
  BrowseSearchQuery,
  DEFAULT_PAGINATION,
  DEFAULT_SORT,
  SortDirection,
  SortField,
  SortState,
} from '../models';
import { FILTER_GROUPS_CONFIG } from '../constants/browse-index.constants';

/**
 * Utility for bidirectional URL query parameter serialization.
 *
 * Handles:
 * - State → URL params (toQueryParams): for Router.navigate()
 * - URL params → State (fromQueryParams): for state restoration on page load
 *
 * Only non-default values are written to the URL to keep it clean.
 * Example: /browse-index?q=أحكام&page=2&library=LIB001,LIB002&sort=-title
 */
export class QueryParamsUtil {

  /** Filter group key → URL param key mapping */
  private static readonly FILTER_PARAM_MAP: Record<string, string> = {
    'items.homebranch': 'library',
    'biblioitem.language': 'lang',
    'biblioitem.itemtype': 'type',
  };

  /** Reverse: URL param key → filter group key */
  private static readonly PARAM_FILTER_MAP: Record<string, string> = (() => {
    const reverseMap: Record<string, string> = {};
    for (const config of FILTER_GROUPS_CONFIG) {
      const paramKey = QueryParamsUtil.FILTER_PARAM_MAP[config.field] ?? config.key;
      reverseMap[paramKey] = config.key;
    }
    return reverseMap;
  })();

  // ═══════════════════════════════════════════════════════════
  //  State → URL params
  // ═══════════════════════════════════════════════════════════

  /**
   * Serialize the current browse state into URL query params.
   * Only includes non-default values to keep URLs clean.
   */
  static toQueryParams(query: BrowseSearchQuery): Params {
    const params: Params = {};

    // Search
    if (query.search && query.search.trim().length > 0) {
      params['q'] = query.search.trim();
    }
    
    // Advanced Search
    if (query.advancedSearch && query.advancedSearch.length > 0) {
      params['adv_q'] = encodeURIComponent(JSON.stringify(query.advancedSearch));
    }

    // Pagination (only include if not default)
    if (query.page > 1) {
      params['page'] = query.page;
    }
    if (query.pageSize !== DEFAULT_PAGINATION.pageSize) {
      params['per_page'] = query.pageSize;
    }

    // Sort (only include if not default relevance)
    if (query.sort.field !== 'relevance') {
      const prefix = query.sort.direction === 'desc' ? '-' : '+';
      params['sort'] = `${prefix}${query.sort.field}`;
    }

    // Checkbox filters
    for (const filter of query.filters) {
      if (filter.values.length > 0) {
        const paramKey = QueryParamsUtil.FILTER_PARAM_MAP[filter.field] ?? filter.field;
        params[paramKey] = filter.values.join(',');
      }
    }

    return params;
  }

  // ═══════════════════════════════════════════════════════════
  //  URL params → State
  // ═══════════════════════════════════════════════════════════

  /**
   * Deserialize URL query params back into state values.
   * Used to restore state on initial page load or browser back/forward.
   */
  static fromQueryParams(params: Params): {
    search: string;
    advancedSearch: any[];
    page: number;
    pageSize: number;
    sort: SortState;
    filtersMap: Map<string, string[]>;
  } {
    let advancedSearch: any[] = [];
    if (params['adv_q']) {
      try {
        advancedSearch = JSON.parse(decodeURIComponent(params['adv_q']));
      } catch (e) {
        advancedSearch = [];
      }
    }
    return {
      search: (params['q'] as string) ?? '',
      advancedSearch,
      page: QueryParamsUtil.parsePositiveInt(params['page'], 1),
      pageSize: QueryParamsUtil.parsePositiveInt(params['per_page'], DEFAULT_PAGINATION.pageSize),
      sort: QueryParamsUtil.parseSortParam(params['sort']),
      filtersMap: QueryParamsUtil.parseFilterParams(params),
    };
  }

  // ═══════════════════════════════════════════════════════════
  //  Private helpers
  // ═══════════════════════════════════════════════════════════

  /**
   * Parse the sort parameter string.
   * Format: "+title" or "-copyright_date" (prefix = direction, rest = field)
   */
  private static parseSortParam(sortParam: string | undefined): SortState {
    if (!sortParam || sortParam.length < 2) {
      return { ...DEFAULT_SORT };
    }

    const direction: SortDirection = sortParam.startsWith('-') ? 'desc' : 'asc';
    const field = sortParam.substring(1) as SortField;

    // Validate that the field is a known sort field
    const validFields: SortField[] = ['title', 'author', 'copyright_date', 'biblio_id'];
    if (!validFields.includes(field)) {
      return { ...DEFAULT_SORT };
    }

    return { field, direction };
  }

  /**
   * Parse filter parameters from the URL.
   * Returns a Map of groupKey → selectedValues[].
   */
  private static parseFilterParams(params: Params): Map<string, string[]> {
    const filtersMap = new Map<string, string[]>();

    for (const [paramKey, groupKey] of Object.entries(QueryParamsUtil.PARAM_FILTER_MAP)) {
      const paramValue = params[paramKey] as string;
      if (paramValue) {
        filtersMap.set(groupKey, paramValue.split(',').filter(v => v.length > 0));
      }
    }

    return filtersMap;
  }

  /**
   * Parse a string to a positive integer, with a default fallback.
   */
  private static parsePositiveInt(value: string | undefined, defaultValue: number): number {
    if (!value) return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) || parsed < 1 ? defaultValue : parsed;
  }

  /**
   * Parse a string to a nullable integer.
   */
  private static parseNullableInt(value: string | undefined): number | null {
    if (!value) return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  }
}
